import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';

// material-ui
import { useTheme } from '@mui/material/styles';
import ButtonBase from '@mui/material/ButtonBase';
import CardContent from '@mui/material/CardContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useDateFilter } from "contexts/DateFilterContext";
import { DateRange } from "react-date-range";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { enGB } from "date-fns/locale";
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import ClearOutlined from "@ant-design/icons/ClearOutlined";
import ReloadOutlined from "@ant-design/icons/ReloadOutlined";
import dayjs from "dayjs";
import { useLocation } from "react-router";
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';


// project imports
import ProfileTab from './ProfileTab';
import SettingTab from './SettingTab';
import Avatar from 'components/@extended/Avatar';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';
import IconButton from 'components/@extended/IconButton';

import useAuth from 'hooks/useAuth';

// assets
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import avatar1 from 'assets/images/users/avatar-1.png';
import { handlerDrawerOpen } from 'api/menu';
// tab panel wrapper
function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`profile-tabpanel-${index}`} aria-labelledby={`profile-tab-${index}`} {...other}>
      {value === index && children}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `profile-tab-${index}`,
    'aria-controls': `profile-tabpanel-${index}`
  };
}

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

export default function Profile() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
const isDashboard = location.pathname.includes("/admin/dashboard");
  const { dateRange, setDateRange, filters, setFilters } = useDateFilter();
const [anchorEl, setAnchorEl] = useState(null);
const openDate = Boolean(anchorEl);

// quick ranges
const quickRanges = [
  { label: "Today", range: () => ({ startDate: new Date(), endDate: new Date() }) },
  { label: "Last 7 Days", range: () => ({ startDate: dayjs().subtract(7, "day").toDate(), endDate: new Date() }) },
  { label: "This Week", range: () => ({ startDate: dayjs().startOf("week").toDate(), endDate: dayjs().endOf("week").toDate() }) },
  { label: "This Month", range: () => ({ startDate: dayjs().startOf("month").toDate(), endDate: dayjs().endOf("month").toDate() }) }
];

const handleDateChange = (ranges) => {
  const { startDate, endDate } = ranges.selection;
  setDateRange([ranges.selection]);
  setFilters({
    start: dayjs(startDate).format("YYYY-MM-DD"),
    end: dayjs(endDate).format("YYYY-MM-DD")
  });
}

  const { logout, user } = useAuth();
  const handleLogout = async () => {
    try {
      await logout();
      navigate(`/login`, {
        state: {
          from: ''
        }
      });
    } catch (err) {
      console.error(err);
    }
  };

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };
const storedUser = (() => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
})();
const userName = storedUser?.name || user?.name || "User";

const handleClose = (event) => {
  if (anchorRef.current && anchorRef.current.contains(event.target)) return;
  setOpen(false);
};


  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };
  // ---- helpers (can be moved to a utils file) ----
function stripBase(pathname, base) {
  const b = String(base || '/').replace(/\/+$/, '');
  if (!b || b === '/') return pathname;
  return pathname.startsWith(b) ? pathname.slice(b.length) || '/' : pathname;
}
function currentPrefix() {
  const base = import.meta.env.VITE_APP_BASE_NAME || '/';
  const effective = stripBase(window.location.pathname, base);
  return /^\/admin(\/|$)/.test(effective) ? '/admin' : '/';
}
function join(base, path) {
  return `${String(base).replace(/\/$/, '')}/${String(path).replace(/^\//, '')}`;
}

 const prefix = currentPrefix();
  const profileHref = prefix === '/admin' ? '/admin/profile' : '/profile';
const goToProfile = () => {
  handlerDrawerOpen(false);   // close drawer
  setOpen(false);             // close Popper
  navigate(profileHref);      // go to profile
};

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      
       <Stack direction="row" spacing={1} alignItems="center">
     {isDashboard && (
        <>
          {/* Date Filter Button */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<CalendarMonthIcon />}
            onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                  height: 34,              // match TextField height
                  minWidth: 220,           // optional: keep it wide enough
                  whiteSpace: "nowrap",    // prevent wrapping
                  textTransform: "none",   // keep proper casing
                  display: "flex",
                  alignItems: "center"
                }}
          >
            {dayjs(dateRange[0].startDate).format("MMM D, YYYY")} - {dayjs(dateRange[0].endDate).format("MMM D, YYYY")}
          </Button>

          {/* Reset */}
          <Tooltip title="Reset Date">
            <IconButton
              onClick={() => {
                const start = dayjs().startOf("month").toDate();
                const end = dayjs().endOf("month").toDate();
                setDateRange([{ startDate: start, endDate: end, key: "selection" }]);
                setFilters({
                  start: dayjs(start).format("YYYY-MM-DD"),
                  end: dayjs(end).format("YYYY-MM-DD")
                });
              }}
            >
              <ClearOutlined />
            </IconButton>
          </Tooltip>

          {/* Popover Calendar */}
          <Popover
            open={openDate}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            <Box sx={{ p: 2 }}>
              {dateRange?.[0]?.startDate && dateRange?.[0]?.endDate ? (
              <DateRange
                editableDateInputs={false}
                onChange={handleDateChange}
                moveRangeOnFirstSelection={false}
                ranges={dateRange}
                locale={enGB}
                rangeColors={[theme.palette.primary.main]}
              />
) : null}
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                {quickRanges.map((qr) => (
                  <Button
                    key={qr.label}
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const { startDate, endDate } = qr.range();
                      handleDateChange({ selection: { startDate, endDate, key: "selection" } });
                      setAnchorEl(null);
                    }}
                  >
                    {qr.label}
                  </Button>
                ))}
              </Stack>
            </Box>
          </Popover>
        </>
      )}

      {/* 🔹 Avatar/Profile Menu */}
      <ButtonBase
        sx={(theme) => ({
          p: 0.25,
          bgcolor: open ? "grey.100" : "transparent",
          borderRadius: 1,
          "&:hover": { bgcolor: "secondary.lighter" },
          "&:focus-visible": {
            outline: `2px solid ${theme.palette.secondary.dark}`,
            outlineOffset: 2
          },
          ...theme.applyStyles("dark", {
            bgcolor: open ? "background.default" : "transparent",
            "&:hover": { bgcolor: "secondary.light" }
          })
        })}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? "profile-grow" : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Stack direction="row" sx={{ gap: 1.25, alignItems: "center", p: 0.5 }}>
          <Avatar
            alt={userName}
            src={storedUser?.picture_url || user?.picture_url || avatar1}
            size="sm" 
           
          />
        </Stack>
      </ButtonBase>
    </Stack>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: 290, minWidth: 240, maxWidth: { xs: 250, md: 290 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false}>
                  <CardContent sx={{ px: 2.5, pt: 1,pb: 1 }}>
                    <Grid container justifyContent="space-between" alignItems="center">
                      <Grid>
                        <Stack direction="row" onClick={goToProfile} sx={{ gap: 1.25, alignItems: 'center' , cursor:'pointer'}} >
                          <Avatar alt="profile user" src={storedUser?.picture_url || user?.picture_url || avatar1} sx={{ width: 32, height: 32  , cursor:'pointer'}}  />
                          <Stack>
                            <Typography variant="h3"  sx={{ cursor:'pointer' }}
                              >{user?.name}</Typography>
                           {prefix === '/admin' ? 'Admin' : `${userName}`}
                          </Stack>
                        </Stack>
                      </Grid>
                      <Grid>
                        <Tooltip title="Logout">
                          <IconButton size="large" sx={{ color: 'text.primary' }} onClick={(e) => {
      e.stopPropagation(); 
      handleLogout();     
    }}>
                            <LogoutOutlined />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </CardContent>

                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  </Box>
                  <TabPanel value={value} index={0} dir={theme.direction}>
                    <ProfileTab handleLogout={handleLogout} />
                  </TabPanel>
                  <TabPanel value={value} index={0} dir={theme.direction}>
                    
                  </TabPanel>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}

TabPanel.propTypes = { children: PropTypes.node, value: PropTypes.number, index: PropTypes.number, other: PropTypes.any };
